import React, { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  LuCalendar,
  LuCog,
  LuCreditCard,
  LuDatabase,
  LuExternalLink,
  LuFileText,
  LuPackage,
  LuPencil,
  LuScissors,
  LuTrash2,
  LuTrendingDown,
  LuTrendingUp,
  LuWrench,
} from "react-icons/lu";

import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  creditCellClass,
  creditTextClass,
  debitCellClass,
  debitTextClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import useConfirmPopup from "../../context/useConfirmPopup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditPayment,
  toggleCreateModal,
} from "../../features/payments/paymentsSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { PaymentsProps } from "../../types/payments";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) => moment(value).format("DD-MM-YYYY");

const formatDateTime = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const formatLongDate = (value: string) =>
  moment(value).format("DD MMMM, YYYY");

const isVoucherRow = (payment: PaymentsProps) => payment.status === 3;


const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const voucherLinkClass =
  "group inline-flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors";

const noteLineClass = "flex items-start gap-1.5 text-sm text-gray-600";

const materialPurchasePath = (id: number) =>
  route.materialPurchaseDetail.replace(":id", id as unknown as string);

const cuttingReceivePath = (id: number) =>
  route.cuttingReceiveDetail.replace(":id", id as unknown as string);

const turningReceivePath = (id: number) =>
  route.turningReceiveDetail.replace(":id", id as unknown as string);

const vmcReceivePath = (id: number) =>
  route.vmcReceiveDetail.replace(":id", id as unknown as string);

const typeHead = (payment: PaymentsProps) => {
  if (payment.status === 1) {
    return (
      <div className="flex items-center gap-1.5">
        <LuCreditCard className="h-3.5 w-3.5 text-green-600" />
        <span className="text-sm font-semibold text-green-900">
          આપેલ પેમેન્ટ
        </span>
      </div>
    );
  }

  if (payment.status === 2) {
    return (
      <div className="flex items-center gap-1.5">
        {payment.type === 0 ? (
          <LuTrendingUp className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <LuTrendingDown className="h-3.5 w-3.5 text-orange-600" />
        )}
        <span className="text-sm font-semibold text-gray-900">વધ ઘટ</span>
      </div>
    );
  }

  // A cutting receive's cutting charges are a payable (જમા) made by the receive itself.
  if (isVoucherRow(payment) && payment.cutting_receive_id) {
    return (
      <div className="flex items-center gap-1.5">
        <LuScissors className="h-3.5 w-3.5 text-teal-600" />
        <span className="text-sm font-semibold text-teal-900">
          કટીંગ રિસીવ
        </span>
      </div>
    );
  }

  // A turning receive's turning charges, like a cutting receive's.
  if (isVoucherRow(payment) && payment.turning_receive_id) {
    return (
      <div className="flex items-center gap-1.5">
        <LuCog className="h-3.5 w-3.5 text-teal-600" />
        <span className="text-sm font-semibold text-teal-900">
          ટર્નિંગ રિસીવ
        </span>
      </div>
    );
  }

  // A VMC receive's VMC charges, like a turning receive's.
  if (isVoucherRow(payment) && payment.vmc_receive_id) {
    return (
      <div className="flex items-center gap-1.5">
        <LuWrench className="h-3.5 w-3.5 text-teal-600" />
        <span className="text-sm font-semibold text-teal-900">
          VMC રિસીવ
        </span>
      </div>
    );
  }

  // A material purchase is a payable (જમા) made by the purchase itself.
  if (isVoucherRow(payment) && payment.material_purchase_id) {
    return (
      <div className="flex items-center gap-1.5">
        <LuPackage className="h-3.5 w-3.5 text-teal-600" />
        <span className="text-sm font-semibold text-teal-900">
          મટીરીયલ ખરીદી
        </span>
      </div>
    );
  }

  // An old bill (status 0), labelled as on the ledger PDF.
  return (
    <div className="flex items-center gap-1.5">
      <LuFileText className="h-3.5 w-3.5 text-teal-600" />
      <span className="text-sm font-semibold text-teal-900">હિસાબ</span>
    </div>
  );
};

const typeDetails = (payment: PaymentsProps) => {
  if (payment.status === 2) {
    return (
      <div className="text-sm text-gray-600">
        {payment.type === 0 ? "અમાઉન્ટ ઉમેરાયું" : "અમાઉન્ટ બાદ થયું"}
      </div>
    );
  }

  if (isVoucherRow(payment) && payment.cutting_receive_id) {
    return (
      <Link
        to={cuttingReceivePath(payment.cutting_receive_id)}
        target="_blank"
        rel="noopener noreferrer"
        className={voucherLinkClass}
      >
        <span className="text-sm font-medium">
          ID: {payment.cutting_receive?.id ?? payment.cutting_receive_id}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">
          {formatLongDate(payment.cutting_receive?.date ?? payment.date)}
        </span>
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  }

  if (isVoucherRow(payment) && payment.turning_receive_id) {
    return (
      <Link
        to={turningReceivePath(payment.turning_receive_id)}
        target="_blank"
        rel="noopener noreferrer"
        className={voucherLinkClass}
      >
        <span className="text-sm font-medium">
          ID: {payment.turning_receive?.id ?? payment.turning_receive_id}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">
          {formatLongDate(payment.turning_receive?.date ?? payment.date)}
        </span>
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  }

  if (isVoucherRow(payment) && payment.vmc_receive_id) {
    return (
      <Link
        to={vmcReceivePath(payment.vmc_receive_id)}
        target="_blank"
        rel="noopener noreferrer"
        className={voucherLinkClass}
      >
        <span className="text-sm font-medium">
          ID: {payment.vmc_receive?.id ?? payment.vmc_receive_id}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">
          {formatLongDate(payment.vmc_receive?.date ?? payment.date)}
        </span>
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  }

  if (isVoucherRow(payment) && payment.material_purchase_id) {
    return (
      <Link
        to={materialPurchasePath(payment.material_purchase_id)}
        target="_blank"
        rel="noopener noreferrer"
        className={voucherLinkClass}
      >
        <span className="text-sm font-medium">
          ID: {payment.material_purchase?.id ?? payment.material_purchase_id}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">
          {formatLongDate(payment.material_purchase?.date ?? payment.date)}
        </span>
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  }

  return null;
};

type Props = {
  payments: PaymentsProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeletePayment: (id: number) => void;
};

const PaymentList = ({
  payments,
  pageNumber,
  pageSize,
  isLoading,
  handleDeletePayment,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.paymentsUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.paymentsDelete
  );

  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 8 + (actionCount > 0 ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditPayment = async (payment: PaymentsProps) => {
    if (payment.status === 1) {
      await dispatch(setEditPayment(payment));
      dispatch(toggleCreateModal(2));
    } else if (payment.status === 2) {
      await dispatch(setEditPayment(payment));
      dispatch(toggleCreateModal(3));
    }
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeletePayment(id);
      setIsActionLoading(0);
    }
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    ક્રમ
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    એકાઉન્ટ ID
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    પ્રકાર
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>વિગતો</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    જમા
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    ઉધાર
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    બેલેન્સ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : payments.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="કોઈ એકાઉન્ટ એન્ટ્રી મળી નથી"
                />
              ) : (
                <TableBody>
                  {payments.map((payment, index) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-medium text-center">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell className="font-medium text-center">
                        {payment.id}
                      </TableCell>

                      <TableCell className="text-center">
                        {payment.type === 0 ? (
                          <p className={creditTextClass}>જમા</p>
                        ) : (
                          <p className={debitTextClass}>ઉધાર</p>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="min-w-0 space-y-1.5">
                          {typeHead(payment)}
                          {typeDetails(payment)}
                          {payment.note && (
                            <div className={noteLineClass}>
                              <LuFileText className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">
                                {payment.note}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className={creditCellClass}>
                        {formatAmount(payment.credit_amount)}
                      </TableCell>

                      <TableCell className={debitCellClass}>
                        {formatAmount(payment.debit_amount)}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(payment.balance)}
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1.5">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(payment.date || payment.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(payment.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === payment.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {payment.isEditable && (
                                  <>
                                    {canUpdate && (
                                      <Button
                                        variant="ghost"
                                        size="iconSm"
                                        title="સંપાદિત કરો"
                                        onClick={() =>
                                          handleEditPayment(payment)
                                        }
                                      >
                                        <LuPencil className="h-4 w-4" />
                                      </Button>
                                    )}

                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="iconSm"
                                        title="કાઢી નાખો"
                                        className="text-red-600 hover:text-red-700"
                                        onClick={() =>
                                          handleDeleteRow(payment.id)
                                        }
                                      >
                                        <LuTrash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : payments.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="કોઈ એકાઉન્ટ એન્ટ્રી મળી નથી" />
          </div>
        ) : (
          payments.map((payment, index) => (
            <div key={payment.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                {typeHead(payment)}
                <span className="shrink-0 text-sm text-gray-500">
                  ID: {payment.id}
                </span>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                {(typeDetails(payment) || payment.note) && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>વિગતો</span>
                    <span className="min-w-0 space-y-1.5 text-right">
                      {typeDetails(payment)}
                      {payment.note && (
                        <span className="block break-words text-gray-600">
                          {payment.note}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>જમા</span>
                  <span className={creditCellClass}>
                    {formatAmount(payment.credit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ઉધાર</span>
                  <span className={debitCellClass}>
                    {formatAmount(payment.debit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>બેલેન્સ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(payment.balance)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(payment.date || payment.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(payment.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && payment.isEditable && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {isActionLoading === payment.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {payment.isEditable && (
                        <>
                          {canUpdate && (
                            <Button
                              variant="outline"
                              title="સંપાદિત કરો"
                              onClick={() => handleEditPayment(payment)}
                            >
                              <LuPencil className="h-4 w-4" />
                            </Button>
                          )}

                          {canDelete && (
                            <Button
                              variant="outline"
                              title="કાઢી નાખો"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteRow(payment.id)}
                            >
                              <LuTrash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default PaymentList;
